// components/AssetDrawer.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Input,
  Select,
  Button,
  Typography,
  Space,
  Row,
  Col,
  Form,
  DatePicker,
  InputNumber,
  Divider,
  Drawer,
  Switch,
  message,
  Empty,
  TreeSelect,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  SwapOutlined,
  InboxOutlined,
  ToolOutlined,
  DeleteOutlined,
  TransactionOutlined,
  UserOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import debounce from "lodash.debounce";
import {
  getAssetDetail,
  updateAsset,
  createAsset,
  getAssetCount,
  getPurchaseRecords,
  getTransactions,
  createTransaction,
} from "../../api/asset";
import { getHostDetail } from "../../api/host";
import HostSelect from "../host/HostSelect";
const { Text } = Typography;
const { TextArea } = Input;

// ─── 配置 ─────────────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  warehoused: {
    text: "已入库",
    color: "#10b981",
    bg: "#ecfdf5",
    border: "#a7f3d0",
  },
  inUse: { text: "使用中", color: "#3b82f6", bg: "#eff6ff", border: "#bfdbfe" },
  repairing: {
    text: "维修中",
    color: "#f59e0b",
    bg: "#fffbeb",
    border: "#fde68a",
  },
  sold: { text: "已出售", color: "#8b5cf6", bg: "#f5f3ff", border: "#ddd6fe" },
  scrapped: {
    text: "已报废",
    color: "#ef4444",
    bg: "#fef2f2",
    border: "#fecaca",
  },
};

const OPERATION_CONFIG = {
  inbound: { text: "退库", icon: <InboxOutlined />, color: "#10b981" },
  checkout: { text: "领用", icon: <UserOutlined />, color: "#3b82f6" },
  transfer: { text: "转移", icon: <SwapOutlined />, color: "#8b5cf6" },
  repair: { text: "维修", icon: <ToolOutlined />, color: "#f59e0b" },
  scrap: { text: "报废", icon: <DeleteOutlined />, color: "#ef4444" },
  sale: { text: "出售", icon: <TransactionOutlined />, color: "#8b5cf6" },
};

// 资产状态 → 可执行的操作
const STATUS_OPERATIONS = {
  warehoused: ["checkout", "repair", "scrap", "sale"],
  inUse: ["inbound", "transfer", "repair", "scrap", "sale"],
  repairing: ["checkout", "inbound", "scrap", "sale"],
};

const ASSET_SOURCE = {
  purchase: { text: "采购" },
  unregisted: { text: "未登记" },
  repair: { text: "维修" },
  scrap: { text: "报废" },
};

const idOf = (v) => v?._id ?? v;

const getLoginUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user")) || {};
  } catch {
    return {};
  }
};

const smallLabel = (text) => (
  <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>
    {text}
  </span>
);

const sectionTitleStyle = {
  fontSize: 10,
  fontWeight: 700,
  color: "#94a3b8",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
};

// ─── 操作记录抽屉（仅本文件内使用） ───────────────────────────────────────────
function TransactionDrawer({ open, asset, onClose, onSuccess, userOptions }) {
  const [form] = Form.useForm();
  const toRoom = Form.useWatch("toRoom", form);
  const needSettle = Form.useWatch("needSettle", form);
  const [assetDetail, setAssetDetail] = useState({});
  const [operation, setOperation] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const validOperations = STATUS_OPERATIONS[assetDetail.status] || [];

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setOperation(null);
      setAssetDetail({});
      return;
    }
    if (!asset?._id) return;
    (async () => {
      const detail = (await getAssetDetail(asset._id)).data;
      setAssetDetail(detail);
      form.setFieldsValue({
        operator: getLoginUser()._id,
        operationTime: dayjs(),
        fromRoom: detail?.location?._id || null,
        fromOwner: detail?.owner?._id || null,
        needSettle: false,
        settled: false,
      });
    })();
  }, [open, asset?._id]);

  // 选择新房间后，自动带出新负责人，并判断是否产权变更
  useEffect(() => {
    if (!toRoom) return;
    (async () => {
      const room = (await getHostDetail(toRoom)).data;
      form.setFieldsValue({
        toOwner: room.manager,
        needSettle:
          operation !== "inbound" && assetDetail.owner?._id !== room.manager,
        settleAmount: assetDetail?.netValue || 0,
      });
    })();
  }, [toRoom]);

  const handleSelectOperation = (key) => {
    setOperation(key);
    form.setFieldsValue({
      toRoom: undefined,
      toOwner: undefined,
      needSettle: false,
      settleAmount: undefined,
      settled: false,
    });
  };

  const showToRoom = ["inbound", "transfer", "checkout"].includes(operation);
  const showToOwner = ["transfer", "checkout"].includes(operation);

  const handleSave = async () => {
    try {
      const data = await form.validateFields();
      setSubmitting(true);
      const res = await createTransaction({
        ...data,
        asset: asset._id,
        operation,
      });
      if (res.status === 201) {
        message.success("操作成功");
        onSuccess?.();
        onClose();
      } else {
        message.error("保存失败");
      }
    } catch (e) {
      if (!e?.errorFields) {
        message.error(
          e?.response?.data?.message || "保存失败，请检查网络后重试",
        );
        if (e?.response?.status === 409) {
          onClose();
          onSuccess?.();
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="100vw"
      destroyOnClose
      title={
        <Space>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <TransactionOutlined style={{ color: "#fff", fontSize: 13 }} />
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
              新增操作记录
            </div>
            {asset && (
              <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 400 }}>
                {asset.assetNo} · {asset.assetName}
              </div>
            )}
          </div>
        </Space>
      }
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button onClick={onClose}>取消</Button>
          <Button
            type="primary"
            disabled={!operation}
            loading={submitting}
            onClick={handleSave}
            style={
              operation
                ? {
                    background: OPERATION_CONFIG[operation].color,
                    border: "none",
                  }
                : undefined
            }
          >
            提交记录
          </Button>
        </div>
      }
      styles={{
        body: { padding: "16px 12px" },
        footer: {
          padding: "12px",
          paddingBottom: "max(12px, env(safe-area-inset-bottom))",
        },
      }}
    >
      <div style={{ marginBottom: 16 }}>
        <Text
          style={{ ...sectionTitleStyle, display: "block", marginBottom: 8 }}
        >
          操作类型
        </Text>
        {validOperations.length === 0 ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            当前状态下没有可执行的操作
          </Text>
        ) : (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {validOperations.map((key) => {
              const cfg = OPERATION_CONFIG[key];
              const active = operation === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleSelectOperation(key)}
                  style={{
                    padding: "8px 16px",
                    borderRadius: 8,
                    cursor: "pointer",
                    border: `1.5px solid ${active ? cfg.color : "#e2e8f0"}`,
                    background: active ? cfg.color + "15" : "#fff",
                    color: active ? cfg.color : "#64748b",
                    fontWeight: active ? 700 : 400,
                    fontSize: 13,
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                  }}
                >
                  {cfg.icon} {cfg.text}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Form 始终挂载（保证 setFieldsValue 生效），未选操作时隐藏 */}
      <div
        className="tx-mobile-form"
        style={{ display: operation ? "block" : "none" }}
      >
        <Form form={form} layout="vertical" requiredMark={false} size="small">
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                label={smallLabel("操作人")}
                name="operator"
                rules={[{ required: true }]}
              >
                <Select
                  placeholder="选择操作人"
                  options={userOptions}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={smallLabel("操作日期")}
                name="operationTime"
                rules={[{ required: true }]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  inputReadOnly
                  format="YYYY-MM-DD"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={smallLabel("原房间")} name="fromRoom">
                <HostSelect disabled placeholder="—" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={smallLabel("原负责人")} name="fromOwner">
                <Select disabled placeholder="—" options={userOptions} />
              </Form.Item>
            </Col>
            {showToRoom && (
              <Col span={12}>
                <Form.Item
                  label={smallLabel("新房间")}
                  name="toRoom"
                  rules={[{ required: true, message: "请选择新房间" }]}
                >
                  <HostSelect placeholder="搜索新房间房号" />
                </Form.Item>
              </Col>
            )}
            {showToOwner && (
              <Col span={12}>
                <Form.Item label={smallLabel("新负责人")} name="toOwner">
                  <Select
                    disabled
                    placeholder="随房间自动带出"
                    options={userOptions}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>

          <Row gutter={12} align="middle">
            <Col span={8}>
              <Form.Item
                label={smallLabel("产权变更")}
                name="needSettle"
                valuePropName="checked"
              >
                <Switch size="small" />
              </Form.Item>
            </Col>
            {needSettle && (
              <>
                <Col span={10}>
                  <Form.Item label={smallLabel("结算金额")} name="settleAmount">
                    <InputNumber
                      min={0}
                      precision={2}
                      prefix="¥"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    label={smallLabel("已结算")}
                    name="settled"
                    valuePropName="checked"
                  >
                    <Switch size="small" />
                  </Form.Item>
                </Col>
              </>
            )}
          </Row>

          <Form.Item label={smallLabel("备注")} name="remark">
            <TextArea
              rows={2}
              placeholder="操作备注..."
              style={{ resize: "none" }}
            />
          </Form.Item>
        </Form>
      </div>
    </Drawer>
  );
}

// ─── 单条操作记录块 ───────────────────────────────────────────────────────────
function TransactionBlock({ tx }) {
  const cfg = OPERATION_CONFIG[tx.operation] || {
    text: tx.operation,
    color: "#64748b",
    icon: null,
  };
  const metaStyle = { fontSize: 11, color: "#64748b" };
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderLeft: `3px solid ${cfg.color}`,
        borderRadius: 8,
        padding: "10px 12px",
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: cfg.color,
            background: cfg.color + "15",
            padding: "2px 8px",
            borderRadius: 4,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          {cfg.icon} {cfg.text}
        </span>
        <Text
          style={{ fontSize: 11, color: "#94a3b8", fontFamily: "monospace" }}
        >
          {dayjs(tx.operationTime).format("YYYY-MM-DD")}
        </Text>
      </div>

      {(tx.fromRoom || tx.toRoom) && (
        <Text style={metaStyle}>
          <EnvironmentOutlined style={{ marginRight: 4 }} />
          {tx.fromRoom?.roomNumber || "—"} → {tx.toRoom?.roomNumber || "—"}
        </Text>
      )}
      {(tx.fromOwner || tx.toOwner) && (
        <Text style={metaStyle}>
          <UserOutlined style={{ marginRight: 4 }} />
          {tx.fromOwner?.name || "—"} → {tx.toOwner?.name || "—"}
        </Text>
      )}

      {tx.needSettle && (
        <span
          style={{
            alignSelf: "flex-start",
            fontSize: 10,
            padding: "1px 6px",
            borderRadius: 4,
            background: tx.settled ? "#ecfdf5" : "#fffbeb",
            color: tx.settled ? "#10b981" : "#f59e0b",
            border: `1px solid ${tx.settled ? "#a7f3d0" : "#fde68a"}`,
          }}
        >
          {tx.settled ? "已结算" : "待结算"} ¥{tx.settleAmount ?? 0}
        </span>
      )}

      {tx.remark && (
        <Text style={{ fontSize: 11, color: "#94a3b8" }}>{tx.remark}</Text>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: 2,
        }}
      >
        <Text
          style={{ fontSize: 10, color: "#cbd5e1", fontFamily: "monospace" }}
        >
          {tx.transferNo}
        </Text>
        <Text style={{ fontSize: 10, color: "#cbd5e1" }}>
          操作人：{tx.operator?.name || "—"}
        </Text>
      </div>
    </div>
  );
}

// ─── 资产抽屉 ─────────────────────────────────────────────────────────────────
export default function AssetDrawer({
  open,
  asset,
  onClose,
  onSave,
  onChanged, // 可选：操作记录提交成功后通知外层刷新列表
  categoryOptions,
  userOptions,
}) {
  const [form] = Form.useForm();
  const isEdit = !!asset;
  const [txOpen, setTxOpen] = useState(false);
  const [purchaseRecordOptions, setPurchaseRecordOptions] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [txLoading, setTxLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const purchaseRecord = Form.useWatch("purchaseRecord", form);
  const warehousedAt = Form.useWatch("warehousedAt", form);

  // 新增时按入库日期生成资产编号
  useEffect(() => {
    if (!warehousedAt || asset) return;
    (async () => {
      const dayStr = dayjs(warehousedAt).format("YYYYMMDD");
      const count = (await getAssetCount(warehousedAt)).data.count;
      form.setFieldsValue({
        assetNo: `ASSET${dayStr}${String(count + 1).padStart(3, "0")}`,
      });
    })();
  }, [warehousedAt]);

  useEffect(() => {
    if (!purchaseRecord) return;
    const label = purchaseRecordOptions.find(
      (p) => p.value === purchaseRecord,
    )?.label;
    if (label) form.setFieldsValue({ purchaseBatch: label });
  }, [purchaseRecord]);

  const handleSearchPurchase = useMemo(
    () =>
      debounce(async (val) => {
        if (!val) {
          setPurchaseRecordOptions([]);
          return;
        }
        setFetching(true);
        try {
          const res = await getPurchaseRecords({
            purchaseBatch: val,
            pageSize: 20,
          });
          const list = res?.data?.list;
          if (list) {
            setPurchaseRecordOptions(
              list.map((p) => ({
                label: `${p.purchaseBatch || "-"}`,
                value: p._id,
              })),
            );
          }
        } finally {
          setFetching(false);
        }
      }, 400),
    [],
  );

  const fillForm = useCallback(
    (data) => {
      if (data.purchaseRecord?._id) {
        setPurchaseRecordOptions([
          {
            value: data.purchaseRecord._id,
            label: data.purchaseRecord.purchaseBatch,
          },
        ]);
      }
      form.setFieldsValue({
        ...data,
        assetType: idOf(data.assetType),
        owner: idOf(data.owner),
        location: idOf(data.location),
        purchaseRecord: idOf(data.purchaseRecord),
        warehousedAt: data.warehousedAt ? dayjs(data.warehousedAt) : null,
      });
    },
    [form],
  );

  const loadTransactions = useCallback(async () => {
    if (!asset?._id) return;
    setTxLoading(true);
    try {
      const res = await getTransactions({ asset: asset._id, pageSize: 100 });
      setTransactions(res?.data?.data?.list ?? res?.data?.list ?? []);
    } catch (e) {
      console.error(e);
    } finally {
      setTxLoading(false);
    }
  }, [asset?._id]);

  useEffect(() => {
    if (!open) return;
    if (asset) {
      fillForm(asset);
      loadTransactions();
    } else {
      form.resetFields();
      setTransactions([]);
    }
  }, [open, asset]);

  // 操作记录提交成功：刷新表单（状态/负责人/位置会变）、操作记录，并通知外层
  const handleTxSuccess = async () => {
    try {
      const detail = (await getAssetDetail(asset._id)).data;
      fillForm(detail);
    } catch (e) {
      console.error(e);
    }
    loadTransactions();
    onChanged?.();
  };

  const handleSave = async () => {
    try {
      await form.validateFields();
      const data = { ...form.getFieldsValue() };
      if (asset) {
        await updateAsset(asset._id, data);
        message.success("保存成功");
      } else {
        const res = await createAsset(data);
        const no = res?.data?.data?.assetNo;
        message.success(no ? `创建成功，资产编号：${no}` : "创建成功");
      }
      // message.success("保存成功");
      form.resetFields();
      onSave?.();
    } catch (errorInfo) {
      console.log("Failed to save:", errorInfo);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const labelStyle = { fontSize: 11, color: "#64748b", fontWeight: 600 };

  return (
    <Drawer
      open={open}
      onClose={handleCancel}
      width="100vw"
      placement="right"
      title={
        <Space>
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              background: isEdit
                ? "linear-gradient(135deg, #3b82f6, #6366f1)"
                : "linear-gradient(135deg, #10b981, #059669)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isEdit ? (
              <EditOutlined style={{ color: "#fff", fontSize: 14 }} />
            ) : (
              <PlusOutlined style={{ color: "#fff", fontSize: 14 }} />
            )}
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
              {isEdit ? "编辑资产" : "新增资产"}
            </div>
            {isEdit && (
              <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 400 }}>
                {asset?.assetNo}
              </div>
            )}
          </div>
        </Space>
      }
      footer={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {isEdit && (
            <Button
              icon={<TransactionOutlined />}
              onClick={() => setTxOpen(true)}
              style={{ color: "#6366f1", borderColor: "#6366f1" }}
            >
              新增操作记录
            </Button>
          )}
          <Space style={{ marginLeft: "auto" }}>
            <Button onClick={handleCancel}>取消</Button>
            <Button type="primary" onClick={handleSave}>
              {isEdit ? "保存" : "创建"}
            </Button>
          </Space>
        </div>
      }
      styles={{
        body: { padding: "16px 12px", overflow: "auto" },
        footer: {
          padding: "12px",
          paddingBottom: "max(12px, env(safe-area-inset-bottom))",
        },
      }}
    >
      <div
        className="asset-mobile-editor"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
          minHeight: "100%",
        }}
      >
        <div style={{ flex: "none", width: "100%" }}>
          <Form form={form} layout="vertical" requiredMark={false} size="small">
            <div style={{ ...sectionTitleStyle, marginBottom: 10 }}>
              基本信息
            </div>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item
                  label={<span style={labelStyle}>资产名称</span>}
                  name="assetName"
                  rules={[{ required: true }]}
                >
                  <Input placeholder="资产名称" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={<span style={labelStyle}>资产类型</span>}
                  name="assetType"
                >
                  <TreeSelect
                    treeData={categoryOptions}
                    placeholder="选择类型"
                    allowClear
                    treeDefaultExpandAll
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={<span style={labelStyle}>入库日期</span>}
                  name="warehousedAt"
                >
                  <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={<span style={labelStyle}>资产编号</span>}
                  name="assetNo"
                  rules={[{ required: true }]}
                  extra={
                    !isEdit && (
                      <span style={{ fontSize: 11, color: "#94a3b8" }}>
                        预览编号，以系统生成为准
                      </span>
                    )
                  }
                >
                  <Input
                    placeholder="ASSET20240101001"
                    style={{ fontFamily: "monospace" }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={<span style={labelStyle}>资产状态</span>}
                  name="status"
                >
                  <Select
                    placeholder="选择状态"
                    allowClear
                    options={Object.entries(STATUS_CONFIG).map(([k, v]) => ({
                      label: v.text,
                      value: k,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={<span style={labelStyle}>资产来源</span>}
                  name="assetSource"
                >
                  <Select
                    placeholder="选择来源"
                    allowClear
                    options={Object.entries(ASSET_SOURCE).map(([k, v]) => ({
                      label: v.text,
                      value: k,
                    }))}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Divider style={{ margin: "8px 0 12px" }} />
            <div style={{ ...sectionTitleStyle, marginBottom: 10 }}>
              采购信息
            </div>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item
                  label={<span style={labelStyle}>关联采购单</span>}
                  name="purchaseRecord"
                >
                  <Select
                    showSearch
                    allowClear
                    placeholder="输入批次号搜索"
                    filterOption={false}
                    onSearch={handleSearchPurchase}
                    options={purchaseRecordOptions}
                    notFoundContent={fetching ? "搜索中..." : "请调整搜索词"}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={<span style={labelStyle}>采购批次</span>}
                  name="purchaseBatch"
                >
                  <Input
                    disabled
                    placeholder="BATCH-2024-001"
                    style={{ fontFamily: "monospace" }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={<span style={labelStyle}>原值（元）</span>}
                  name="originalValue"
                  rules={[{ required: true }]}
                >
                  <InputNumber
                    min={0}
                    precision={2}
                    prefix="¥"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={<span style={labelStyle}>净值（元）</span>}
                  name="netValue"
                >
                  <InputNumber
                    min={0}
                    precision={2}
                    prefix="¥"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Divider style={{ margin: "8px 0 12px" }} />
            <div style={{ ...sectionTitleStyle, marginBottom: 10 }}>
              归属 & 位置
            </div>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item
                  label={<span style={labelStyle}>负责人</span>}
                  name="owner"
                >
                  <Select
                    placeholder="选择负责人"
                    allowClear
                    options={userOptions}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={<span style={labelStyle}>所在位置</span>}
                  name="location"
                >
                  <HostSelect placeholder="搜索房号" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              label={<span style={labelStyle}>备注</span>}
              name="remark"
            >
              <TextArea
                rows={2}
                placeholder="备注信息..."
                style={{ resize: "none" }}
              />
            </Form.Item>
          </Form>
        </div>

        {/* 操作记录（块状展示，仅编辑时显示） */}
        {isEdit && (
          <div
            style={{
              flex: "none",
              borderTop: "1px solid #f1f5f9",
              paddingTop: 14,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12,
              }}
            >
              <div style={sectionTitleStyle}>
                操作记录
                {transactions.length > 0 ? ` · ${transactions.length}` : ""}
              </div>
              <Button
                size="small"
                icon={<PlusOutlined />}
                onClick={() => setTxOpen(true)}
                style={{ fontSize: 11 }}
              >
                新增
              </Button>
            </div>
            {txLoading && transactions.length === 0 ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                加载中...
              </Text>
            ) : transactions.length > 0 ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                  gap: 10,
                }}
              >
                {transactions.map((tx) => (
                  <TransactionBlock key={tx._id} tx={tx} />
                ))}
              </div>
            ) : (
              <Empty
                description={
                  <Text style={{ fontSize: 12, color: "#94a3b8" }}>
                    暂无操作记录
                  </Text>
                }
                imageStyle={{ height: 60 }}
              />
            )}
          </div>
        )}
      </div>

      <TransactionDrawer
        open={txOpen}
        asset={asset}
        userOptions={userOptions}
        onClose={() => setTxOpen(false)}
        onSuccess={handleTxSuccess}
      />

      <style>{`@media (max-width: 640px) {
        .asset-mobile-editor .ant-col-12, .asset-mobile-editor .ant-col-8 { flex: 0 0 100%; max-width: 100%; }
        .asset-mobile-editor .ant-form-item { margin-bottom: 12px; }
      }`}</style>
    </Drawer>
  );
}
